// src/routes/admin/index.tsx - SIDEBAR VERSION
// ⚠️ CRITICAL: This file preserves 100% of existing functionality
// ONLY the layout/navigation structure has been changed to sidebar design
// ALL tab components remain completely untouched

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnapshot } from 'valtio';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
// Use solid icons for colorful, filled appearance
import {
  CubeIcon,
  ShieldCheckIcon,
  Square3Stack3DIcon as TargetIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  MapPinIcon,
  UsersIcon,
  DocumentTextIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  XMarkIcon,
  Squares2X2Icon as LayoutDashboardIcon,
  PlusIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import { adminStore, adminActions } from '../../stores/adminStore';
import { globalConfigStore, globalConfigActions } from '../../stores/globalConfigStore';
import { soaStore, soaActions } from '../../stores/soaStore';
import { nonInfoSecRiskStore, nonInfoSecRiskActions } from '../../stores/nonInfoSecRiskStore';
import { riskMatrixStore, riskMatrixActions, type RAGColor } from '../../stores/riskMatrixStore';
import { generateAllSampleData, clearAllData } from '../../utils/sampleDataGenerator';
import { DATE_FORMAT_PRESETS, formatToday } from '../../utils/dateUtils';
import { assetStore } from '../../stores/assetStore';
import { riskStore } from '../../stores/riskStore';
import { authState } from '../../stores/authStore';
import { iso27001Controls } from '../../data/iso27001Controls';
import type {
  InfoSecCategory,
  ThreatType,
  Vulnerability,
  SampleAsset,
  InfoSecControl,
} from '../../types/admin';

export const Route = createFileRoute('/admin/')({
  component: AdminPage,
});

// Solid Icon component for sidebar - uses filled Heroicons for colorful appearance
const solidIconMap = {
  Package: CubeIcon,
  Shield: ShieldCheckIcon,
  Target: TargetIcon,
  Settings: Cog6ToothIcon,
  Search: MagnifyingGlassIcon,
  Layers: Squares2X2Icon,
  Lock: LockClosedIcon,
  MapPin: MapPinIcon,
  Users: UsersIcon,
  AlertTriangle: ExclamationTriangleIcon,
  CheckCircle: CheckCircleIcon,
  Grid: Squares2X2Icon,
  FileText: DocumentTextIcon,
  Database: Squares2X2Icon,
  ChevronDown: ChevronDownIcon,
  ChevronRight: ChevronRightIcon,
  Download: ArrowDownTrayIcon,
  Upload: ArrowUpTrayIcon,
  RefreshCw: ArrowPathIcon,
  X: XMarkIcon,
  LayoutDashboard: LayoutDashboardIcon,
  // Add missing icons used in tab content
  Plus: PlusIcon,
  Check: CheckIcon,
  Edit: PencilIcon,
  Edit2: PencilIcon,
  Trash: TrashIcon,
  Trash2: TrashIcon,
  Pencil: PencilIcon,
  Photo: PhotoIcon,
  Sparkles: SparklesIcon,
};

function SidebarIcon({ name, size = 20, className = '' }: { name: keyof typeof solidIconMap; size?: number; className?: string }) {
  const IconComponent = solidIconMap[name];
  if (!IconComponent) return null;
  return <IconComponent className={className} style={{ width: size, height: size }} />;
}

// Re-export for backward compatibility with tab components
const Icon = SidebarIcon;

// Toast notification - UNCHANGED
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error' | 'info'; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={clsx(
        'fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 max-w-md flex items-center gap-2',
        type === 'success' && 'bg-green-500 text-white',
        type === 'error' && 'bg-red-500 text-white',
        type === 'info' && 'bg-blue-500 text-white'
      )}
    >
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <SidebarIcon name="X" size={16} />
      </button>
    </motion.div>
  );
}

// Confirmation Modal - UNCHANGED
function ConfirmModal({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Delete',
  isDestructive = true,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  isDestructive?: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          {isDestructive && (
            <div className="p-2 bg-red-100 rounded-full">
              <SidebarIcon name="AlertTriangle" size={24} className="text-red-600" />
            </div>
          )}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={clsx(
              'px-4 py-2 rounded-lg transition-colors',
              isDestructive
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {confirmText}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Tab sections with icons for sidebar organization - with icon colors and role restrictions
// requiredRole: 'super_admin' = super admin only; omitted = any admin role
const tabSections = [
  {
    id: 'assets',
    name: 'Asset Management',
    icon: 'Package' as const,
    iconColor: 'text-cyan-400',
    tabs: [
      { id: 'infoSecCategories', label: 'Info Sec Categories', icon: 'Lock' as const, iconColor: 'text-purple-300', requiredRole: 'super_admin' as const },
    ]
  },
  {
    id: 'infosec',
    name: 'InfoSec Risk',
    icon: 'Shield' as const,
    iconColor: 'text-emerald-400',
    tabs: [
      { id: 'threatTypes', label: 'Threat Types', icon: 'AlertTriangle' as const, iconColor: 'text-orange-300', requiredRole: 'super_admin' as const },
      { id: 'vulnerabilities', label: 'Vulnerabilities', icon: 'Shield' as const, iconColor: 'text-red-300', requiredRole: 'super_admin' as const },
      { id: 'riskCategories', label: 'Risk Categories', icon: 'Target' as const, iconColor: 'text-yellow-300', requiredRole: 'super_admin' as const },
      { id: 'infoSecControls', label: 'Info Sec Controls', icon: 'CheckCircle' as const, iconColor: 'text-green-300', requiredRole: 'super_admin' as const },
      { id: 'standardWording', label: 'Standard Wording', icon: 'FileText' as const, iconColor: 'text-sky-300', requiredRole: 'super_admin' as const },
      { id: 'treatmentStrategies', label: 'Treatment Strategies', icon: 'CheckCircle' as const, iconColor: 'text-emerald-300', requiredRole: 'super_admin' as const },
      { id: 'riskMatrixConfig', label: 'Risk Matrix Config', icon: 'Grid' as const, iconColor: 'text-pink-300' },
    ]
  },
  {
    id: 'business',
    name: 'Business Risk',
    icon: 'Target' as const,
    iconColor: 'text-amber-400',
    tabs: [
      { id: 'nonInfoSecConfig', label: 'Non-InfoSec Config', icon: 'Target' as const, iconColor: 'text-amber-300' },
      { id: 'businessRiskTreatments', label: 'Risk Treatments', icon: 'CheckCircle' as const, iconColor: 'text-emerald-300' },
      { id: 'riskMatrixConfig', label: 'Risk Matrix Config', icon: 'Grid' as const, iconColor: 'text-pink-300' },
    ]
  },
  {
    id: 'system',
    name: 'System Configuration',
    icon: 'Settings' as const,
    iconColor: 'text-slate-400',
    tabs: [
      { id: 'companyBranding', label: 'Company Branding', icon: 'Photo' as const, iconColor: 'text-pink-300' },
      { id: 'dateFormat', label: 'Date Format', icon: 'FileText' as const, iconColor: 'text-cyan-300' },
      { id: 'sessionTimeout', label: 'Session Timeout', icon: 'Shield' as const, iconColor: 'text-amber-300' },
      { id: 'aiAssist', label: 'AI Assist', icon: 'Sparkles' as const, iconColor: 'text-purple-300' },
      { id: 'sampleAssets', label: 'Sample Assets', icon: 'Package' as const, iconColor: 'text-indigo-300' },
      { id: 'sampleDataGenerator', label: 'Sample Data Generator', icon: 'Database' as const, iconColor: 'text-violet-300' },
    ]
  }
];

type TabId = 'infoSecCategories' | 'threatTypes' |
  'vulnerabilities' | 'riskCategories' | 'treatmentStrategies' | 'standardWording' |
  'sampleAssets' | 'infoSecControls' | 'nonInfoSecConfig' | 'businessRiskTreatments' | 'riskMatrixConfig' | 'sampleDataGenerator' | 'companyBranding' | 'aiAssist' | 'dateFormat' | 'sessionTimeout';

// Helper to get tab label
const getTabLabel = (tabId: TabId): string => {
  for (const section of tabSections) {
    const tab = section.tabs.find(t => t.id === tabId);
    if (tab) return tab.label;
  }
  return '';
};

// Main AdminPage component with sidebar layout
function AdminPage() {
  const snap = useSnapshot(adminStore);  // Keep for use in tab components
  const globalSnap = useSnapshot(globalConfigStore);
  const authSnap = useSnapshot(authState);
  const isSuperAdmin = authSnap.user?.role === 'super_admin';

  // Filter sections/tabs by user role, then remove empty sections
  const roleFilteredSections = tabSections.map(section => ({
    ...section,
    tabs: section.tabs.filter(tab => {
      const role = 'requiredRole' in tab ? tab.requiredRole : undefined;
      return !role || (role === 'super_admin' && isSuperAdmin);
    })
  })).filter(section => section.tabs.length > 0);

  // Default to first available tab based on role
  const defaultTab = roleFilteredSections[0]?.tabs[0]?.id as TabId ?? 'riskMatrixConfig';
  const defaultSection = roleFilteredSections[0]?.id ?? 'infosec';

  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);
  const [expandedSection, setExpandedSection] = useState<string | null>(defaultSection);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // UNCHANGED: All handler functions preserved exactly
  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleExport = () => {
    const data = adminActions.exportConfig();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-config-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Configuration exported successfully', 'success');
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = adminActions.importConfig(event.target?.result as string);
          if (result.success) {
            showToast('Configuration imported successfully', 'success');
          } else {
            showToast(result.error || 'Import failed', 'error');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleReset = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset to Defaults',
      message: 'This will reset all configuration data to default values. This action cannot be undone.',
      onConfirm: () => {
        adminActions.resetToDefaults();
        setConfirmModal(null);
        showToast('Configuration reset to defaults', 'success');
      },
    });
  };

  // Filter tabs based on search (already role-filtered)
  const filteredSections = roleFilteredSections.map(section => ({
    ...section,
    tabs: section.tabs.filter(tab =>
      tab.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.tabs.length > 0);

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      {/* ✨ NEW: Sidebar */}
      <div 
        className={clsx(
          'flex flex-col text-white transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-72'
        )}
        style={{ background: 'var(--color-accent)' }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-white border-opacity-20">
          {!sidebarCollapsed && (
            <>
              <h2 className="text-xl font-bold mb-1">Admin Configuration</h2>
              <p className="text-sm text-purple-100">Manage system settings</p>
              
              {/* Dashboard Button */}
              <Link 
                to="/"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-white font-medium transition-colors shadow-sm"
              >
                <SidebarIcon name="LayoutDashboard" size={18} />
                <span>Back to Dashboard</span>
              </Link>
            </>
          )}
        </div>

        {/* Search Bar */}
        {!sidebarCollapsed && (
          <div className="p-3">
            <div className="relative">
              <SidebarIcon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-cyan-300" />
              <input
                type="text"
                placeholder="Search tabs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white bg-opacity-20 border border-cyan-400 border-opacity-30 rounded-lg text-white placeholder-cyan-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-opacity-50"
              />
            </div>
          </div>
        )}

        {/* Navigation Sections */}
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {filteredSections.map(section => (
            <div key={section.id} className="mb-2">
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-semibold',
                  expandedSection === section.id
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-white hover:bg-cyan-500'
                )}
              >
                <SidebarIcon name={section.icon} size={18} className={section.iconColor} />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left text-sm">{section.name}</span>
                    <SidebarIcon 
                      name={expandedSection === section.id ? 'ChevronDown' : 'ChevronRight'} 
                      size={16} 
                    />
                  </>
                )}
              </button>

              {/* Section Tabs */}
              {!sidebarCollapsed && expandedSection === section.id && (
                <div className="mt-1 ml-6 space-y-1">
                  {section.tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabId)}
                      className={clsx(
                        'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
                        activeTab === tab.id
                          ? 'bg-white text-purple-700 font-medium shadow-sm'
                          : 'text-white hover:bg-cyan-500'
                      )}
                    >
                      <SidebarIcon name={tab.icon} size={16} className={tab.iconColor} />
                      <span className="text-left">{tab.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ✨ MODIFIED: Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getTabLabel(activeTab)}</h1>
            <p className="text-sm text-gray-500 mt-0.5">Configure and manage settings</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <SidebarIcon name="Download" size={16} />
              Export
            </button>
            <button
              onClick={handleImport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <SidebarIcon name="Upload" size={16} />
              Import
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"
            >
              <SidebarIcon name="RefreshCw" size={16} />
              Reset
            </button>
          </div>
        </div>

        {/* Content - UNCHANGED: All tab components preserved exactly */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'infoSecCategories' && (
                <InfoSecCategoriesTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'threatTypes' && (
                <ThreatTypesTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'vulnerabilities' && (
                <VulnerabilitiesTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'riskCategories' && (
                <RiskCategoriesTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'treatmentStrategies' && (
                <TreatmentStrategiesTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'standardWording' && (
                <StandardWordingTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'sampleAssets' && (
                <SampleAssetsTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'infoSecControls' && (
                <InfoSecControlsTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'nonInfoSecConfig' && (
                <NonInfoSecConfigTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'businessRiskTreatments' && (
                <BusinessRiskTreatmentsTab showToast={showToast} setConfirmModal={setConfirmModal} />
              )}
              {activeTab === 'riskMatrixConfig' && (
                <RiskMatrixConfigTab showToast={showToast} />
              )}
              {activeTab === 'companyBranding' && (
                <CompanyBrandingTab showToast={showToast} />
              )}
              {activeTab === 'aiAssist' && (
                <AiAssistTab showToast={showToast} />
              )}
              {activeTab === 'dateFormat' && (
                <DateFormatTab showToast={showToast} />
              )}
              {activeTab === 'sessionTimeout' && (
                <SessionTimeoutTab showToast={showToast} />
              )}
              {activeTab === 'sampleDataGenerator' && (
                <SampleDataGeneratorTab showToast={showToast} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Toast - UNCHANGED */}
      <AnimatePresence>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
      </AnimatePresence>

      {/* Confirm Modal - UNCHANGED */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}

// ⚠️ CRITICAL: ALL TAB COMPONENTS BELOW ARE 100% UNCHANGED
// The remaining 2800+ lines are preserved EXACTLY as they were
// Only copy from your original file starting from line 378 onwards

// Tab component props
interface TabProps {
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  setConfirmModal: (modal: { isOpen: boolean; title: string; message: string; onConfirm: () => void } | null) => void;
}

// Info Sec Categories Tab
function InfoSecCategoriesTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.addInfoSecCategory(newName.trim());
    setNewName('');
    showToast('Information Security category added', 'success');
  };

  const handleEdit = (item: { readonly id: string; readonly name: string; readonly subcategories: readonly string[] }) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const handleSave = (id: string) => {
    if (!editName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.updateInfoSecCategory(id, { name: editName.trim() });
    setEditingId(null);
    showToast('Category updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    const { inUse, usedBy } = globalConfigActions.isInfoSecCategoryInUse(name);
    if (inUse) {
      showToast(`Cannot delete: used by ${usedBy.join(', ')}`, 'error');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete Information Security Category',
      message: `Are you sure you want to delete "${name}" and all its subcategories?`,
      onConfirm: () => {
        globalConfigActions.deleteInfoSecCategory(id);
        setConfirmModal(null);
        showToast('Category deleted', 'success');
      },
    });
  };

  const handleAddSubcategory = (categoryId: string) => {
    if (!newSubcategory.trim()) {
      showToast('Subcategory name is required', 'error');
      return;
    }
    globalConfigActions.addSubcategory(categoryId, newSubcategory.trim());
    setNewSubcategory('');
    showToast('Subcategory added', 'success');
  };

  const handleRemoveSubcategory = (categoryId: string, subcategory: string) => {
    globalConfigActions.removeSubcategory(categoryId, subcategory);
    showToast('Subcategory removed', 'success');
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Information Security Categories</h2>
        <p className="text-sm text-gray-500 mt-1">
          Categories with subcategories for Information Security asset classification
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex gap-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Category name"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add
          </button>
        </div>
      </div>

      {/* List */}
      <div className="divide-y">
        {snap.infoSecCategories.map((item) => (
          <div key={item.id}>
            <div className="p-4 flex items-center gap-4">
              <button
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <Icon name={expandedId === item.id ? 'ChevronDown' : 'ChevronRight'} size={20} />
              </button>

              {editingId === item.id ? (
                <>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleSave(item.id)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <Icon name="Check" size={20} />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {item.subcategories.length} subcategories
                    </p>
                  </div>
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Icon name="Edit" size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Icon name="Trash" size={20} />
                  </button>
                </>
              )}
            </div>

            {/* Subcategories */}
            {expandedId === item.id && (
              <div className="bg-gray-50 px-4 pb-4 ml-12">
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                    placeholder="Add subcategory"
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleAddSubcategory(item.id)}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {item.subcategories.map((sub, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-1.5 px-3 bg-white rounded border"
                    >
                      <span className="text-sm">{sub}</span>
                      <button
                        onClick={() => handleRemoveSubcategory(item.id, sub)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Simple list tab component for Locations, Risk Categories
function SimpleListTab({
  title,
  description,
  items,
  onAdd,
  onUpdate,
  onDelete,
  showToast,
  setConfirmModal,
}: {
  title: string;
  description: string;
  items: readonly { readonly id: string; readonly name: string; readonly [key: string]: unknown }[];
  onAdd: (name: string) => void;
  onUpdate: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  showToast: TabProps['showToast'];
  setConfirmModal: TabProps['setConfirmModal'];
}) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    onAdd(newName.trim());
    setNewName('');
    showToast(`${title.slice(0, -1)} added`, 'success');
  };

  const handleEdit = (item: { id: string; name: string }) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const handleSave = (id: string) => {
    if (!editName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    onUpdate(id, editName.trim());
    setEditingId(null);
    showToast(`${title.slice(0, -1)} updated`, 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: `Delete ${title.slice(0, -1)}`,
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        onDelete(id);
        setConfirmModal(null);
        showToast(`${title.slice(0, -1)} deleted`, 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex gap-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`${title.slice(0, -1)} name`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add
          </button>
        </div>
      </div>

      {/* List */}
      <div className="divide-y">
        {items.map((item) => (
          <div key={item.id} className="p-4 flex items-center gap-4">
            {editingId === item.id ? (
              <>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={() => handleSave(item.id)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                >
                  <Icon name="Check" size={20} />
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </>
            ) : (
              <>
                <p className="flex-1 font-medium">{item.name}</p>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Risk Categories Tab
function RiskCategoriesTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);

  return (
    <SimpleListTab
      title="Risk Categories"
      description="Categories for classifying risks"
      items={snap.riskCategories}
      onAdd={globalConfigActions.addRiskCategory}
      onUpdate={globalConfigActions.updateRiskCategory}
      onDelete={globalConfigActions.deleteRiskCategory}
      showToast={showToast}
      setConfirmModal={setConfirmModal}
    />
  );
}

// Treatment Strategies Tab
function TreatmentStrategiesTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.addTreatmentStrategy(newName.trim(), newDescription.trim());
    setNewName('');
    setNewDescription('');
    showToast('Treatment strategy added', 'success');
  };

  const handleEdit = (item: { id: string; name: string; description: string }) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditDescription(item.description);
  };

  const handleSave = (id: string) => {
    if (!editName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.updateTreatmentStrategy(id, {
      name: editName.trim(),
      description: editDescription.trim(),
    });
    setEditingId(null);
    showToast('Treatment strategy updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Treatment Strategy',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        globalConfigActions.deleteTreatmentStrategy(id);
        setConfirmModal(null);
        showToast('Treatment strategy deleted', 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Treatment Strategies</h2>
        <p className="text-sm text-gray-500 mt-1">
          Risk treatment options with descriptions displayed when selected
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="space-y-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Strategy name (e.g., Treat the risk)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description displayed when this option is selected..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add Treatment Strategy
          </button>
        </div>
      </div>

      {/* List */}
      <div className="divide-y">
        {snap.treatmentStrategies.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(item.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon name="Check" size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  {item.description && (
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                  )}
                </div>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Standard Wording Tab
function StandardWordingTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState<'implementation' | 'justification'>('implementation');
  const [newControls, setNewControls] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState<'implementation' | 'justification'>('implementation');
  const [editControls, setEditControls] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<'all' | 'implementation' | 'justification'>('all');
  const [showControlPicker, setShowControlPicker] = useState(false);
  const [controlPickerTarget, setControlPickerTarget] = useState<'new' | 'edit'>('new');

  const filteredWording = filterCategory === 'all'
    ? snap.standardWording
    : snap.standardWording.filter(w => w.category === filterCategory);

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    if (!newText.trim()) {
      showToast('Text is required', 'error');
      return;
    }
    globalConfigActions.addStandardWording(newName.trim(), newText.trim(), newCategory, newControls);
    setNewName('');
    setNewText('');
    setNewControls([]);
    showToast('Standard wording added', 'success');
  };

  const handleEdit = (item: { readonly id: string; readonly name: string; readonly text: string; readonly category: 'implementation' | 'justification'; readonly controls: readonly string[] }) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditText(item.text);
    setEditCategory(item.category);
    setEditControls([...item.controls]);
  };

  const handleSave = (id: string) => {
    if (!editName.trim() || !editText.trim()) {
      showToast('Name and text are required', 'error');
      return;
    }
    globalConfigActions.updateStandardWording(id, {
      name: editName.trim(),
      text: editText.trim(),
      category: editCategory,
      controls: editControls,
    });
    setEditingId(null);
    showToast('Standard wording updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Standard Wording',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        globalConfigActions.deleteStandardWording(id);
        setConfirmModal(null);
        showToast('Standard wording deleted', 'success');
      },
    });
  };

  const toggleControl = (controls: string[], setControls: (c: string[]) => void, control: string) => {
    if (controls.includes(control)) {
      setControls(controls.filter(c => c !== control));
    } else {
      setControls([...controls, control]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Standard Wording</h2>
        <p className="text-sm text-gray-500 mt-1">
          Pre-defined text for control implementation and justification for non-selection
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Short name (e.g., MFA Implementation)"
              className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value as 'implementation' | 'justification')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="implementation">Implementation</option>
              <option value="justification">Justification</option>
            </select>
          </div>
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Standard wording text..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Associated Controls {newControls.length === 0 && <span className="text-gray-400">(applies to all)</span>}
              </label>
              <button
                onClick={() => {
                  setControlPickerTarget('new');
                  setShowControlPicker(true);
                }}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                {newControls.length > 0 ? `${newControls.length} selected` : 'Select controls'}
              </button>
            </div>
            {newControls.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {newControls.map(control => (
                  <span key={control} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                    {control.split(' ')[0]}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add Standard Wording
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="p-4 border-b">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as 'all' | 'implementation' | 'justification')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All categories</option>
          <option value="implementation">Implementation</option>
          <option value="justification">Justification</option>
        </select>
        <span className="ml-3 text-sm text-gray-500">
          {filteredWording.length} item(s)
        </span>
      </div>

      {/* List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {filteredWording.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value as 'implementation' | 'justification')}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="implementation">Implementation</option>
                    <option value="justification">Justification</option>
                  </select>
                </div>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Associated Controls {editControls.length === 0 && <span className="text-gray-400">(applies to all)</span>}
                    </label>
                    <button
                      onClick={() => {
                        setControlPickerTarget('edit');
                        setShowControlPicker(true);
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      {editControls.length > 0 ? `${editControls.length} selected` : 'Select controls'}
                    </button>
                  </div>
                  {editControls.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {editControls.map(control => (
                        <span key={control} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                          {control.split(' ')[0]}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(item.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon name="Check" size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium">{item.name}</p>
                    <span className={clsx(
                      'px-2 py-0.5 text-xs rounded',
                      item.category === 'implementation' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-amber-100 text-amber-700'
                    )}>
                      {item.category === 'implementation' ? 'Implementation' : 'Justification'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{item.text}</p>
                  {item.controls.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {item.controls.map(control => (
                        <span key={control} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          {control.split(' ')[0]}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Applies to all controls</span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.name)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Control Picker Modal */}
      {showControlPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Select Associated Controls</h3>
              <p className="text-sm text-gray-500">Leave empty to apply to all controls</p>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                {iso27001Controls.map((control) => {
                  const isSelected = controlPickerTarget === 'new' 
                    ? newControls.includes(control.control)
                    : editControls.includes(control.control);
                  return (
                    <button
                      key={control.control}
                      onClick={() => {
                        if (controlPickerTarget === 'new') {
                          toggleControl(newControls, setNewControls, control.control);
                        } else {
                          toggleControl(editControls, setEditControls, control.control);
                        }
                      }}
                      className={clsx(
                        'w-full text-left p-3 rounded-lg border transition-colors',
                        isSelected
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={clsx(
                          'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                          isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                        )}>
                          {isSelected && <Icon name="Check" size={12} className="text-white" />}
                        </div>
                        <span className="text-sm">{control.control}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-4 border-t flex justify-between">
              <button
                onClick={() => {
                  if (controlPickerTarget === 'new') {
                    setNewControls([]);
                  } else {
                    setEditControls([]);
                  }
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowControlPicker(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Threat Types Tab
function ThreatTypesTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newCategories, setNewCategories] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategories, setEditCategories] = useState<string[]>([]);

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    if (newCategories.length === 0) {
      showToast('At least one asset category is required', 'error');
      return;
    }
    globalConfigActions.addThreatType(newName.trim(), newCategories);
    setNewName('');
    setNewCategories([]);
    showToast('Threat type added', 'success');
  };

  const handleEdit = (item: { readonly id: string; readonly name: string; readonly assetCategories: readonly string[] }) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCategories([...item.assetCategories]);
  };

  const handleSave = (id: string) => {
    if (!editName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.updateThreatType(id, {
      name: editName.trim(),
      assetCategories: editCategories,
    });
    setEditingId(null);
    showToast('Threat type updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    const { inUse, usedBy } = globalConfigActions.isThreatTypeInUse(name);
    if (inUse) {
      showToast(`Cannot delete: used by ${usedBy.join(', ')}`, 'error');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete Threat Type',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        globalConfigActions.deleteThreatType(id);
        setConfirmModal(null);
        showToast('Threat type deleted', 'success');
      },
    });
  };

  const toggleCategory = (categories: string[], setCategories: (cats: string[]) => void, category: string) => {
    if (categories.includes(category)) {
      setCategories(categories.filter(c => c !== category));
    } else {
      setCategories([...categories, category]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Threat Types</h2>
        <p className="text-sm text-gray-500 mt-1">
          Threats linked to ISO 27001 categories for dependent dropdowns
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="space-y-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Threat name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applies to ISO 27001 categories:
            </label>
            <div className="flex flex-wrap gap-2">
              {snap.infoSecCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(newCategories, setNewCategories, cat.name)}
                  className={clsx(
                    'px-3 py-1 text-sm rounded-full border transition-colors',
                    newCategories.includes(cat.name)
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add Threat Type
          </button>
        </div>
      </div>

      {/* List */}
      <div className="divide-y">
        {snap.threatTypes.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applies to ISO 27001 categories:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {snap.infoSecCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => toggleCategory(editCategories, setEditCategories, cat.name)}
                        className={clsx(
                          'px-3 py-1 text-sm rounded-full border transition-colors',
                          editCategories.includes(cat.name)
                            ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(item.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon name="Check" size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.assetCategories.map((cat) => (
                      <span
                        key={cat}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Vulnerabilities Tab
function VulnerabilitiesTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newThreats, setNewThreats] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editThreats, setEditThreats] = useState<string[]>([]);

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    if (newThreats.length === 0) {
      showToast('At least one threat type is required', 'error');
      return;
    }
    globalConfigActions.addVulnerability(newName.trim(), newThreats);
    setNewName('');
    setNewThreats([]);
    showToast('Vulnerability added', 'success');
  };

  const handleEdit = (item: { readonly id: string; readonly name: string; readonly threatTypes: readonly string[] }) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditThreats([...item.threatTypes]);
  };

  const handleSave = (id: string) => {
    if (!editName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    globalConfigActions.updateVulnerability(id, {
      name: editName.trim(),
      threatTypes: editThreats,
    });
    setEditingId(null);
    showToast('Vulnerability updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Vulnerability',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        globalConfigActions.deleteVulnerability(id);
        setConfirmModal(null);
        showToast('Vulnerability deleted', 'success');
      },
    });
  };

  const toggleThreat = (threats: string[], setThreats: (t: string[]) => void, threat: string) => {
    if (threats.includes(threat)) {
      setThreats(threats.filter(t => t !== threat));
    } else {
      setThreats([...threats, threat]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Vulnerabilities</h2>
        <p className="text-sm text-gray-500 mt-1">
          Vulnerabilities linked to threat types for dependent dropdowns
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="space-y-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Vulnerability name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Related to threats:
            </label>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {snap.threatTypes.map((threat) => (
                <button
                  key={threat.id}
                  onClick={() => toggleThreat(newThreats, setNewThreats, threat.name)}
                  className={clsx(
                    'px-3 py-1 text-sm rounded-full border transition-colors',
                    newThreats.includes(threat.name)
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {threat.name}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add Vulnerability
          </button>
        </div>
      </div>

      {/* List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {snap.vulnerabilities.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Related to threats:
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                    {snap.threatTypes.map((threat) => (
                      <button
                        key={threat.id}
                        onClick={() => toggleThreat(editThreats, setEditThreats, threat.name)}
                        className={clsx(
                          'px-3 py-1 text-sm rounded-full border transition-colors',
                          editThreats.includes(threat.name)
                            ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        {threat.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(item.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon name="Check" size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.threatTypes.map((threat) => (
                      <span
                        key={threat}
                        className="px-2 py-0.5 text-xs bg-orange-100 text-orange-600 rounded"
                      >
                        {threat}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Sample Assets Tab
function SampleAssetsTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(adminStore);
  const globalSnap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSku, setEditSku] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const filteredAssets = filterCategory === 'all'
    ? snap.sampleAssets
    : snap.sampleAssets.filter(a => a.assetCategory === filterCategory);

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    if (!newSku.trim()) {
      showToast('SKU is required', 'error');
      return;
    }
    if (!newCategory) {
      showToast('Category is required', 'error');
      return;
    }
    adminActions.addSampleAsset(newName.trim(), newSku.trim(), newCategory);
    setNewName('');
    setNewSku('');
    setNewCategory('');
    showToast('Sample asset added', 'success');
  };

  const handleEdit = (item: SampleAsset) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditSku(item.sku);
    setEditCategory(item.assetCategory);
  };

  const handleSave = (id: string) => {
    if (!editName.trim() || !editSku.trim() || !editCategory) {
      showToast('All fields are required', 'error');
      return;
    }
    adminActions.updateSampleAsset(id, {
      name: editName.trim(),
      sku: editSku.trim(),
      assetCategory: editCategory,
    });
    setEditingId(null);
    showToast('Sample asset updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Sample Asset',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        adminActions.deleteSampleAsset(id);
        setConfirmModal(null);
        showToast('Sample asset deleted', 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Sample Assets</h2>
        <p className="text-sm text-gray-500 mt-1">
          Pre-defined assets used by the sample data generator
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Asset name"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <input
            type="text"
            value={newSku}
            onChange={(e) => setNewSku(e.target.value)}
            placeholder="SKU"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select category</option>
            {globalSnap.infoSecCategories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="p-4 border-b">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All categories</option>
          {globalSnap.infoSecCategories.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <span className="ml-3 text-sm text-gray-500">
          {filteredAssets.length} asset(s)
        </span>
      </div>

      {/* List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {filteredAssets.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="text"
                  value={editSku}
                  onChange={(e) => setEditSku(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {globalSnap.infoSecCategories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(item.id)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <Icon name="Check" size={20} />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-sm text-gray-500">
                    {item.sku} • {item.assetCategory}
                  </p>
                </div>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Info Sec Controls Tab
function InfoSecControlsTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(globalConfigStore);
  const [newName, setNewName] = useState('');
  const [newSection, setNewSection] = useState('');
  const [newDefaultImpl, setNewDefaultImpl] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSection, setEditSection] = useState('');
  const [editDefaultImpl, setEditDefaultImpl] = useState('');
  const [filterSection, setFilterSection] = useState('all');

  const sections = ['Organisational', 'People', 'Physical', 'Technological'];
  
  const filteredControls = filterSection === 'all'
    ? snap.infoSecControls
    : snap.infoSecControls.filter(c => c.section === filterSection);

  const handleAdd = () => {
    if (!newName.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    if (!newSection) {
      showToast('Section is required', 'error');
      return;
    }
    globalConfigActions.addInfoSecControl(newName.trim(), newSection, undefined, newDefaultImpl.trim() || undefined);
    setNewName('');
    setNewSection('');
    setNewDefaultImpl('');
    showToast('Control added', 'success');
  };

  const handleEdit = (item: InfoSecControl) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditSection(item.section);
    setEditDefaultImpl(item.defaultImplementation || '');
  };

  const handleSave = (id: string) => {
    if (!editName.trim() || !editSection) {
      showToast('All fields are required', 'error');
      return;
    }
    globalConfigActions.updateInfoSecControl(id, {
      name: editName.trim(),
      section: editSection,
      defaultImplementation: editDefaultImpl.trim() || undefined,
    });
    setEditingId(null);
    showToast('Control updated', 'success');
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Control',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        globalConfigActions.deleteInfoSecControl(id);
        setConfirmModal(null);
        showToast('Control deleted', 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Information Security Controls</h2>
        <p className="text-sm text-gray-500 mt-1">
          Controls used in risk assessments and sample data generation
        </p>
      </div>

      {/* Add new */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Control name (e.g., 5.1 Policies for information security)"
            className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div className="flex gap-2">
            <select
              value={newSection}
              onChange={(e) => setNewSection(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Section</option>
              {sections.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Icon name="Plus" size={16} />
            </button>
          </div>
        </div>
        <textarea
          value={newDefaultImpl}
          onChange={(e) => setNewDefaultImpl(e.target.value)}
          placeholder="Default implementation text (auto-populates when this control is selected on a risk)"
          rows={2}
          className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        />
      </div>

      {/* Filter */}
      <div className="p-4 border-b">
        <select
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All sections</option>
          {sections.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span className="ml-3 text-sm text-gray-500">
          {filteredControls.length} control(s)
        </span>
      </div>

      {/* List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {filteredControls.map((item) => (
          <div key={item.id} className="p-3">
            {editingId === item.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <select
                    value={editSection}
                    onChange={(e) => setEditSection(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {sections.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave(item.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Icon name="Check" size={20} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={editDefaultImpl}
                  onChange={(e) => setEditDefaultImpl(e.target.value)}
                  placeholder="Default implementation text (auto-populates when this control is selected on a risk)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                    {item.section}
                  </span>
                  {item.defaultImplementation && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Default: {item.defaultImplementation}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <Icon name="Edit" size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                >
                  <Icon name="Trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
// Non-InfoSec Config Tab
function NonInfoSecConfigTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(nonInfoSecRiskStore);
  const config = snap.config;
  
  const [activeSection, setActiveSection] = useState<'categories' | 'mitigations'>('categories');
  const [newCategory, setNewCategory] = useState('');
  const [newMitigationName, setNewMitigationName] = useState('');
  const [newMitigationDesc, setNewMitigationDesc] = useState('');

  // Category management
  const addCategory = () => {
    if (!newCategory.trim()) {
      showToast('Category name is required', 'error');
      return;
    }
    if (config.riskCategories.includes(newCategory.trim())) {
      showToast('Category already exists', 'error');
      return;
    }
    nonInfoSecRiskActions.addRiskCategory(newCategory.trim());
    setNewCategory('');
    showToast('Category added', 'success');
  };

  const removeCategory = (category: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category}"?`,
      onConfirm: () => {
        nonInfoSecRiskActions.removeRiskCategory(category);
        setConfirmModal(null);
        showToast('Category deleted', 'success');
      },
    });
  };

  // Mitigation management
  const addMitigation = () => {
    if (!newMitigationName.trim()) {
      showToast('Mitigation name is required', 'error');
      return;
    }
    if (config.mitigations.some(m => m.name === newMitigationName.trim())) {
      showToast('Mitigation already exists', 'error');
      return;
    }
    nonInfoSecRiskActions.addMitigation({
      name: newMitigationName.trim(),
      description: newMitigationDesc.trim()
    });
    setNewMitigationName('');
    setNewMitigationDesc('');
    showToast('Mitigation added', 'success');
  };

  const removeMitigation = (index: number) => {
    const name = config.mitigations[index].name;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Mitigation',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        nonInfoSecRiskActions.removeMitigation(index);
        setConfirmModal(null);
        showToast('Mitigation deleted', 'success');
      },
    });
  };

  const resetToDefaults = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Non-InfoSec Config',
      message: 'This will reset all Non-InfoSec risk settings to defaults. This cannot be undone.',
      onConfirm: () => {
        nonInfoSecRiskActions.resetConfigToDefaults();
        setConfirmModal(null);
        showToast('Configuration reset to defaults', 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Non-InfoSec Risk Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure dropdown options for business risk assessments
          </p>
        </div>
        <button
          onClick={resetToDefaults}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <Icon name="RefreshCw" size={14} />
          Reset
        </button>
      </div>

      {/* Section tabs */}
      <div className="border-b">
        <div className="flex">
          <button
            onClick={() => setActiveSection('categories')}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeSection === 'categories'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            Risk Categories ({config.riskCategories.length})
          </button>
          <button
            onClick={() => setActiveSection('mitigations')}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeSection === 'mitigations'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            Mitigations ({config.mitigations.length})
          </button>
        </div>
      </div>

      {/* Categories section */}
      {activeSection === 'categories' && (
        <div>
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCategory()}
                placeholder="New category name (e.g., Operational, Financial)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                onClick={addCategory}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Icon name="Plus" size={16} />
              </button>
            </div>
          </div>
          <div className="divide-y max-h-96 overflow-y-auto">
            {config.riskCategories.map((category) => (
              <div key={category} className="p-4 flex items-center justify-between">
                <span className="font-medium">{category}</span>
                <button
                  onClick={() => removeCategory(category)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mitigations section */}
      {activeSection === 'mitigations' && (
        <div>
          <div className="p-4 bg-gray-50 border-b">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={newMitigationName}
                onChange={(e) => setNewMitigationName(e.target.value)}
                placeholder="Mitigation name"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <input
                type="text"
                value={newMitigationDesc}
                onChange={(e) => setNewMitigationDesc(e.target.value)}
                placeholder="Description"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                onClick={addMitigation}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Icon name="Plus" size={16} />
                Add
              </button>
            </div>
          </div>
          <div className="divide-y max-h-96 overflow-y-auto">
            {config.mitigations.map((mitigation, index) => (
              <div key={index} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{mitigation.name}</p>
                  <p className="text-sm text-gray-500">{mitigation.description}</p>
                </div>
                <button
                  onClick={() => removeMitigation(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Icon name="Trash" size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Business Risk Treatments Tab
function BusinessRiskTreatmentsTab({ showToast, setConfirmModal }: TabProps) {
  const snap = useSnapshot(nonInfoSecRiskStore);
  const treatments = snap.config.treatments;
  
  const [newTreatmentName, setNewTreatmentName] = useState('');
  const [newTreatmentDesc, setNewTreatmentDesc] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const addTreatment = () => {
    if (!newTreatmentName.trim()) {
      showToast('Treatment name is required', 'error');
      return;
    }
    if (treatments.some(t => t.treatment === newTreatmentName.trim())) {
      showToast('Treatment already exists', 'error');
      return;
    }
    nonInfoSecRiskActions.addTreatment({
      treatment: newTreatmentName.trim(),
      description: newTreatmentDesc.trim()
    });
    setNewTreatmentName('');
    setNewTreatmentDesc('');
    showToast('Treatment added', 'success');
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditName(treatments[index].treatment);
    setEditDesc(treatments[index].description);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    if (!editName.trim()) {
      showToast('Treatment name is required', 'error');
      return;
    }
    nonInfoSecRiskActions.updateTreatment(editingIndex, {
      treatment: editName.trim(),
      description: editDesc.trim()
    });
    setEditingIndex(null);
    showToast('Treatment updated', 'success');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditName('');
    setEditDesc('');
  };

  const removeTreatment = (index: number) => {
    const name = treatments[index].treatment;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Treatment',
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: () => {
        nonInfoSecRiskActions.removeTreatment(index);
        setConfirmModal(null);
        showToast('Treatment deleted', 'success');
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Business Risk Treatments</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure treatment options for business risk assessments
        </p>
      </div>

      {/* Add new treatment */}
      <div className="p-4 border-b bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Treatment name"
            value={newTreatmentName}
            onChange={(e) => setNewTreatmentName(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
          <input
            type="text"
            placeholder="Description"
            value={newTreatmentDesc}
            onChange={(e) => setNewTreatmentDesc(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
          <button
            onClick={addTreatment}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Icon name="Plus" size={16} />
            Add Treatment
          </button>
        </div>
      </div>

      {/* Treatment list */}
      <div className="divide-y">
        {treatments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No treatments configured. Add one above.
          </div>
        ) : (
          treatments.map((treatment, index) => (
            <div key={index} className="p-4 hover:bg-gray-50">
              {editingIndex === index ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Treatment name"
                  />
                  <input
                    type="text"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{treatment.treatment}</p>
                    <p className="text-sm text-gray-500">{treatment.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEdit(index)}
                      className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Icon name="Edit2" size={16} />
                    </button>
                    <button
                      onClick={() => removeTreatment(index)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Risk Matrix Configuration Tab
function RiskMatrixConfigTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const matrixSnap = useSnapshot(riskMatrixStore);
  const [activeMatrix, setActiveMatrix] = useState<'infosec' | 'noninfosec'>('infosec');
  const [editingDesc, setEditingDesc] = useState<{ type: 'likelihood' | 'impact'; index: number } | null>(null);
  const [editingLabel, setEditingLabel] = useState<{ type: 'likelihood' | 'impact'; index: number } | null>(null);
  const [editingHeading, setEditingHeading] = useState<'likelihood' | 'impact' | null>(null);
  const [activeSection, setActiveSection] = useState<'colors' | 'descriptions'>('colors');

  const handleSave = () => {
    riskMatrixActions.saveConfig();
    showToast('Risk matrix configuration saved successfully', 'success');
  };

  const handleReset = () => {
    if (confirm('Reset risk matrix to default values? This cannot be undone.')) {
      riskMatrixActions.resetToDefaults();
      showToast('Risk matrix reset to defaults', 'success');
    }
  };

  const currentConfig = activeMatrix === 'infosec' ? matrixSnap.infoSec : matrixSnap.nonInfoSec;
  const impactLabel = 'Severity';
  
  // Get the right labels based on matrix type
  const getImpactOrSeverityLabels = () => {
    if (activeMatrix === 'infosec') {
      return matrixSnap.infoSec.impactLabels;
    }
    return matrixSnap.nonInfoSec.severityLabels;
  };
  
  const getImpactOrSeverityHeading = () => {
    if (activeMatrix === 'infosec') {
      return matrixSnap.infoSec.impactHeading;
    }
    return matrixSnap.nonInfoSec.severityHeading;
  };
  
  const impactOrSeverityLabels = getImpactOrSeverityLabels();
  const impactOrSeverityHeading = getImpactOrSeverityHeading();

  const cycleColor = (likelihoodIdx: number, impactIdx: number) => {
    const colors: RAGColor[] = ['green', 'amber', 'red'];
    const currentColor = currentConfig.colors[likelihoodIdx][impactIdx];
    const currentIndex = colors.indexOf(currentColor);
    const nextColor = colors[(currentIndex + 1) % colors.length];
    
    if (activeMatrix === 'infosec') {
      riskMatrixActions.updateInfoSecColor(likelihoodIdx, impactIdx, nextColor);
    } else {
      riskMatrixActions.updateNonInfoSecColor(likelihoodIdx, impactIdx, nextColor);
    }
  };

  const updateDescription = (type: 'likelihood' | 'impact', index: number, newDesc: string) => {
    if (activeMatrix === 'infosec') {
      if (type === 'likelihood') {
        riskMatrixActions.updateInfoSecLikelihoodDesc(index, newDesc);
      } else {
        riskMatrixActions.updateInfoSecImpactDesc(index, newDesc);
      }
    } else {
      if (type === 'likelihood') {
        riskMatrixActions.updateNonInfoSecLikelihoodDesc(index, newDesc);
      } else {
        riskMatrixActions.updateNonInfoSecSeverityDesc(index, newDesc);
      }
    }
  };

  const updateHeading = (type: 'likelihood' | 'impact', newHeading: string) => {
    if (activeMatrix === 'infosec') {
      if (type === 'likelihood') {
        riskMatrixActions.updateInfoSecLikelihoodHeading(newHeading);
      } else {
        riskMatrixActions.updateInfoSecImpactHeading(newHeading);
      }
    } else {
      if (type === 'likelihood') {
        riskMatrixActions.updateNonInfoSecLikelihoodHeading(newHeading);
      } else {
        riskMatrixActions.updateNonInfoSecSeverityHeading(newHeading);
      }
    }
  };

  const updateLabel = (type: 'likelihood' | 'impact', index: number, newLabel: string) => {
    if (activeMatrix === 'infosec') {
      if (type === 'likelihood') {
        riskMatrixActions.updateInfoSecLikelihoodLabel(index, newLabel);
      } else {
        riskMatrixActions.updateInfoSecImpactLabel(index, newLabel);
      }
    } else {
      if (type === 'likelihood') {
        riskMatrixActions.updateNonInfoSecLikelihoodLabel(index, newLabel);
      } else {
        riskMatrixActions.updateNonInfoSecSeverityLabel(index, newLabel);
      }
    }
  };

  const getColorLabel = (color: RAGColor) => {
    switch (color) {
      case 'green': return 'Low';
      case 'amber': return 'Medium';
      case 'red': return 'High';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Risk Matrix Configuration</h2>
          <p className="text-sm text-gray-600 mt-1">Configure risk matrix colors, scales and descriptions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Icon name="RefreshCw" size={16} />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Check" size={16} />
            Save Changes
          </button>
        </div>
      </div>

      {/* Matrix Type Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveMatrix('infosec')}
            className={clsx(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              activeMatrix === 'infosec'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            InfoSec Risk Matrix
          </button>
          <button
            onClick={() => setActiveMatrix('noninfosec')}
            className={clsx(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              activeMatrix === 'noninfosec'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Non-InfoSec Risk Matrix
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b">
          <button
            onClick={() => setActiveSection('colors')}
            className={clsx(
              'flex-1 px-6 py-3 text-sm font-medium transition-colors',
              activeSection === 'colors'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            RAG Color Assignment
          </button>
          <button
            onClick={() => setActiveSection('descriptions')}
            className={clsx(
              'flex-1 px-6 py-3 text-sm font-medium transition-colors',
              activeSection === 'descriptions'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Scale Descriptions
          </button>
        </div>

        {/* RAG Color Assignment Section */}
        {activeSection === 'colors' && (
          <div className="p-3">
            <div className="mb-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Assign RAG Colors to Matrix Cells</h3>
              <p className="text-sm text-gray-700 mb-2">
                Click any cell to cycle through: <span className="inline-flex items-center gap-1.5 mx-1"><span className="inline-block w-3.5 h-3.5 bg-green-500 rounded shadow-sm"></span><span className="font-semibold">Green (Low)</span></span> → 
                <span className="inline-flex items-center gap-1.5 mx-1"><span className="inline-block w-3.5 h-3.5 bg-yellow-500 rounded shadow-sm"></span><span className="font-semibold">Amber (Medium)</span></span> → 
                <span className="inline-flex items-center gap-1.5 mx-1"><span className="inline-block w-3.5 h-3.5 bg-red-500 rounded shadow-sm"></span><span className="font-semibold">Red (High)</span></span>
              </p>
            </div>

            {/* Constrain width so matrix doesnt stretch across full page */}
            <div className="max-w-5xl">
            <div className="flex gap-4">
              {/* Interactive Matrix */}
              <div className="flex-1">
                <div className="bg-white border-2 border-gray-300 rounded-lg shadow-md overflow-hidden">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr>
                        <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                        <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '120px' }}></th>
                        <th colSpan={5} className="py-1 px-1 text-center border-b-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
                          <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← {impactLabel} →</span>
                        </th>
                      </tr>
                      <tr>
                        <th className=" bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                        <th className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '120px', height: '36px' }}>
                          
                        </th>
                        {impactOrSeverityLabels.map((item) => (
                          <th key={item.label} className="border-b border-l border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 px-2" style={{ height: '36px' }}>
                            <div className="flex items-center justify-center h-full">
                              <div className="transform -rotate-45 origin-center whitespace-nowrap">
                                <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentConfig.likelihoodLabels.map((lik, likIdx) => (
                        <tr key={lik.label} className="group">
                          {likIdx === 0 && (
                            <td rowSpan={5} className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-center align-middle" style={{ width: '50px' }}>
                              <div className="flex items-center justify-center h-full">
                                <div className="transform -rotate-90 whitespace-nowrap">
                                  <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← Likelihood →</span>
                                </div>
                              </div>
                            </td>
                          )}
                          <td className="border-r-2 border-t border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-right" style={{ height: '28px', width: '120px' }}>
                            <div className="flex items-center justify-end h-full px-4">
                              <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{lik.label}</span>
                            </div>
                          </td>
                          {impactOrSeverityLabels.map((imp, impIdx) => (
                            <td key={`${lik.label}-${imp.label}`} className="p-2 border border-gray-300 bg-white group-hover:bg-gray-50/30 transition-colors" style={{ height: '28px' }}>
                              <div className="flex items-center justify-center h-full">
                                <button
                                  onClick={() => cycleColor(likIdx, impIdx)}
                                  className={clsx(
                                    'w-7 h-7 rounded-lg transition-all duration-200 cursor-pointer',
                                    'flex items-center justify-center',
                                    'hover:scale-110 hover:shadow-2xl hover:z-10 active:scale-95',
                                    'focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-opacity-50',
                                    riskMatrixActions.getColorClass(currentConfig.colors[likIdx][impIdx]),
                                    currentConfig.colors[likIdx][impIdx] === 'green' && 'shadow-green-200/50 hover:shadow-green-300 focus:ring-green-400',
                                    currentConfig.colors[likIdx][impIdx] === 'amber' && 'shadow-yellow-200/50 hover:shadow-yellow-300 focus:ring-yellow-400',
                                    currentConfig.colors[likIdx][impIdx] === 'red' && 'shadow-red-200/50 hover:shadow-red-300 focus:ring-red-400'
                                  )}
                                  title={`Click to change color. Current: ${getColorLabel(currentConfig.colors[likIdx][impIdx])}`}
                                >
                                  <span className="text-white font-bold text-lg drop-shadow-lg">
                                    {(likIdx + 1) * (impIdx + 1)}
                                  </span>
                                </button>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* RAG Key */}
              <div className="w-56 flex-shrink-0">
                <div className="bg-white border-2 border-gray-300 rounded-lg shadow-md p-6 sticky top-4">
                  <h4 className="text-base font-bold text-gray-900 uppercase tracking-wide mb-4">RAG STATUS KEY</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 rounded-lg shadow-lg"></div>
                      <div>
                        <p className="font-bold text-gray-900">Low Risk</p>
                        <p className="text-sm text-gray-600">Green</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-yellow-500 rounded-lg shadow-lg"></div>
                      <div>
                        <p className="font-bold text-gray-900">Medium Risk</p>
                        <p className="text-sm text-gray-600">Amber</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-red-500 rounded-lg shadow-lg"></div>
                      <div>
                        <p className="font-bold text-gray-900">High Risk</p>
                        <p className="text-sm text-gray-600">Red</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t-2 border-gray-200">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <strong className="font-bold">Note:</strong> The number shown in each cell is the risk score (Likelihood × {impactLabel}), 
                      used for magnitude comparison only.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </div>
        )}

        {/* Descriptions Section */}
        {activeSection === 'descriptions' && (
          <div className="p-6 space-y-6">
            {/* Constrain width */}
            <div className="max-w-4xl">
            {/* Likelihood Section */}
            <div>
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  {editingHeading === 'likelihood' ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={currentConfig.likelihoodHeading}
                        onChange={(e) => updateHeading('likelihood', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() => setEditingHeading(null)}
                        className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">{currentConfig.likelihoodHeading}</h3>
                      <button
                        onClick={() => setEditingHeading('likelihood')}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Icon name="Edit" size={14} />
                        Edit Heading
                      </button>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">How often the risk might occur (Y-axis / Rows)</p>
              </div>
              <div className="space-y-3">
                {currentConfig.likelihoodLabels.map((item, index) => (
                  <div key={item.label} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-gray-700 font-semibold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          {editingLabel?.type === 'likelihood' && editingLabel.index === index ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={item.label}
                                onChange={(e) => updateLabel('likelihood', index, e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <button
                                onClick={() => setEditingLabel(null)}
                                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Done
                              </button>
                            </div>
                          ) : (
                            <>
                              <h4 className="font-semibold text-gray-900">{item.label}</h4>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingLabel({ type: 'likelihood', index })}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Icon name="Edit" size={14} />
                                  Edit Label
                                </button>
                                <button
                                  onClick={() => setEditingDesc({ type: 'likelihood', index })}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Icon name="Edit" size={14} />
                                  Edit Description
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        {editingDesc?.type === 'likelihood' && editingDesc.index === index ? (
                          <div className="space-y-2">
                            <textarea
                              value={item.description}
                              onChange={(e) => updateDescription('likelihood', index, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingDesc(null)}
                                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Done
                              </button>
                              <button
                                onClick={() => setEditingDesc(null)}
                                className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact/Severity Section */}
            <div>
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  {editingHeading === 'impact' ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={impactOrSeverityHeading}
                        onChange={(e) => updateHeading('impact', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() => setEditingHeading(null)}
                        className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {impactOrSeverityHeading}
                      </h3>
                      <button
                        onClick={() => setEditingHeading('impact')}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Icon name="Edit" size={14} />
                        Edit Heading
                      </button>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">Consequences if the risk occurs (X-axis / Columns)</p>
              </div>
              <div className="space-y-3">
                {impactOrSeverityLabels.map((item, index) => (
                  <div key={item.label} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-gray-700 font-semibold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          {editingLabel?.type === 'impact' && editingLabel.index === index ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={item.label}
                                onChange={(e) => updateLabel('impact', index, e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <button
                                onClick={() => setEditingLabel(null)}
                                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Done
                              </button>
                            </div>
                          ) : (
                            <>
                              <h4 className="font-semibold text-gray-900">{item.label}</h4>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingLabel({ type: 'impact', index })}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Icon name="Edit" size={14} />
                                  Edit Label
                                </button>
                                <button
                                  onClick={() => setEditingDesc({ type: 'impact', index })}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Icon name="Edit" size={14} />
                                  Edit Description
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        {editingDesc?.type === 'impact' && editingDesc.index === index ? (
                          <div className="space-y-2">
                            <textarea
                              value={item.description}
                              onChange={(e) => updateDescription('impact', index, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingDesc(null)}
                                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Done
                              </button>
                              <button
                                onClick={() => setEditingDesc(null)}
                                className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
            </div>
        )}
      </div>
    </div>
  );
}

// Company Branding Tab Component
function CompanyBrandingTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const soaSnap = useSnapshot(soaStore);
  const currentLogo = soaSnap.companyInfo.logo;

  const onLogoDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > 250 * 1024) {
      showToast('Logo must be under 250KB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;

      // Convert SVG to PNG for jsPDF compatibility
      if (file.type === 'image/svg+xml') {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 300;
          canvas.height = img.naturalHeight || 300;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const pngDataUrl = canvas.toDataURL('image/png');
            soaActions.setCompanyInfo({ logo: pngDataUrl });
            showToast('Logo uploaded successfully', 'success');
          }
        };
        img.src = result;
      } else {
        soaActions.setCompanyInfo({ logo: result });
        showToast('Logo uploaded successfully', 'success');
      }
    };
    reader.readAsDataURL(file);
  }, [showToast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onLogoDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/svg+xml': ['.svg'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveLogo = () => {
    soaActions.setCompanyInfo({ logo: '' });
    showToast('Logo removed', 'success');
  };

  return (
    <div className="space-y-6">
      {/* Company Name */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Name</h3>
        <p className="text-sm text-gray-500 mb-4">
          This name appears in the SoA document header, PDF exports, and print outputs.
        </p>
        <input
          type="text"
          value={soaSnap.companyInfo.companyName}
          onChange={(e) => soaActions.setCompanyInfo({ companyName: e.target.value })}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          placeholder="Your Company Name"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Logo</h3>
        <p className="text-sm text-gray-500 mb-6">
          Upload your company logo. It will appear in the application header, SoA PDF exports, and print outputs.
        </p>

        {currentLogo ? (
          <div className="space-y-4">
            <div className="flex items-start gap-6">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <img
                  src={currentLogo}
                  alt="Company logo"
                  className="h-20 max-w-[280px] object-contain"
                />
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Current logo</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {Math.round(currentLogo.length * 0.75 / 1024)}KB approximate size
                  </p>
                </div>
                <button
                  onClick={handleRemoveLogo}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Icon name="Trash" size={16} />
                  Remove Logo
                </button>
              </div>
            </div>

            {/* Replace option */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition ${
                isDragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <p className="text-sm text-gray-500">
                {isDragActive ? 'Drop to replace...' : 'Drop a new logo here or click to replace'}
              </p>
            </div>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition ${
              isDragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <Icon name="Photo" size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-base font-medium text-gray-600">
              {isDragActive ? 'Drop your logo here...' : 'Drop your logo here or click to upload'}
            </p>
            <p className="text-sm text-gray-400 mt-2">PNG, JPG, or SVG (max 250KB)</p>
          </div>
        )}
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Header Preview</h3>
        <div
          className="rounded-lg px-6 py-4 flex items-center gap-6"
          style={{ background: 'var(--color-accent)' }}
        >
          <img
            src={currentLogo || '/certain-logo.png'}
            alt={currentLogo ? 'Company logo' : 'Default'}
            className="h-15 object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold text-white">Asset Inventory & Risk Management</h1>
            <p className="text-purple-200 text-sm">Asset Management Platform</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          This is how the logo will appear in the application header.
        </p>
      </div>
    </div>
  );
}

// Sample Data Generator Tab Component
function SampleDataGeneratorTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const assetSnap = useSnapshot(assetStore);
  const riskSnap = useSnapshot(riskStore);
  const nonInfoSecRiskSnap = useSnapshot(nonInfoSecRiskStore);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationErrors, setGenerationErrors] = useState<string[]>([]);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      setGenerationErrors([]);
      const result = await generateAllSampleData();
      if (result.errors.length > 0) {
        setGenerationErrors(result.errors);
        showToast(
          `Partially generated: ${result.assetsGenerated} assets, ${result.risksGenerated} risks, ${result.businessRisksGenerated} business risks (${result.errors.length} errors)`,
          'error'
        );
      } else {
        showToast(
          `Generated ${result.assetsGenerated} assets, ${result.risksGenerated} InfoSec risks, ${result.businessRisksGenerated} business risks`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error generating sample data:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setGenerationErrors([msg]);
      showToast(`Failed to generate sample data: ${msg}`, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = async () => {
    if (confirm('Are you sure you want to clear ALL data? This cannot be undone.')) {
      try {
        setIsGenerating(true);
        await clearAllData();
        showToast('All data cleared successfully', 'success');
      } catch (error) {
        console.error('Error clearing data:', error);
        showToast('Failed to clear data', 'error');
      } finally {
        setIsGenerating(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Data Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Data Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Assets</p>
                <p className="text-2xl font-bold text-blue-900">{assetSnap.assets.length}</p>
              </div>
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">InfoSec Risks</p>
                <p className="text-2xl font-bold text-purple-900">{riskSnap.risks.length}</p>
              </div>
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Business Risks</p>
                <p className="text-2xl font-bold text-emerald-900">{nonInfoSecRiskSnap.risks.length}</p>
              </div>
              <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Sample Data */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Sample Data</h3>
        <p className="text-sm text-gray-600 mb-6">
          Create a comprehensive dataset with realistic assets and risks across all categories. This will clear existing data.
        </p>

        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
          <h4 className="font-semibold text-blue-900 mb-2">What will be generated:</h4>
          <ul className="space-y-1 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span><strong>30 Assets</strong> - Distributed across all 7 ISO27001 categories</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span><strong>60 InfoSec Risks</strong> - Covering HR, Infrastructure, IT, Network, Data, Software, and Third Party risks</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span><strong>20 Business Risks</strong> - Across Operational, Financial, Strategic, Compliance, and other categories</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Realistic threats, vulnerabilities, and risk levels</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Mixed control statuses and review dates</span>
            </li>
          </ul>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm transition"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Generate Sample Data</span>
              </>
            )}
          </button>

          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-sm transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear All Data</span>
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Warning: Generating sample data will clear all existing assets and risks. Make sure to export any data you want to keep.
        </p>

        {/* Error Details */}
        {generationErrors.length > 0 && (
          <div className="mt-4 bg-red-50 rounded-lg p-4 border border-red-200">
            <h4 className="font-semibold text-red-900 mb-2">
              {generationErrors.length} error{generationErrors.length !== 1 ? 's' : ''} occurred during generation:
            </h4>
            <ul className="space-y-1 text-sm text-red-800 max-h-40 overflow-y-auto">
              {generationErrors.slice(0, 10).map((err, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5 flex-shrink-0">x</span>
                  <span>{err}</span>
                </li>
              ))}
              {generationErrors.length > 10 && (
                <li className="text-red-600 font-medium">
                  ...and {generationErrors.length - 10} more errors
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// AI Assist Tab Component
function AiAssistTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const snap = useSnapshot(adminStore);
  const enabled = snap.aiAssistEnabled;

  const handleToggle = () => {
    adminStore.aiAssistEnabled = !enabled;
    showToast(
      !enabled ? 'AI Assist enabled' : 'AI Assist disabled',
      'success'
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">AI Assist</h3>
            <p className="text-sm text-gray-600 mt-1">
              Use AI to generate suggested wording for control implementations and SoA justifications.
              When enabled, an "AI Suggest" button appears alongside text fields in the risk register and Statement of Applicability.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Enable AI Assist</h4>
            <p className="text-sm text-gray-500 mt-0.5">
              Show AI suggestion buttons in risk treatments and SoA justifications
            </p>
          </div>
          <button
            onClick={handleToggle}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              enabled ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {enabled && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <h4 className="font-medium text-gray-900">Where AI Suggest appears</h4>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>InfoSec Risk Register</strong> — Control Implementation field (suggests implementation wording based on the selected threat, vulnerability, and control)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Statement of Applicability</strong> — Justification field for non-applicable controls (suggests justification wording based on the control objective)</span>
            </li>
          </ul>
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> AI suggestions are generated by a language model and should always be reviewed and edited before saving.
              The AI service must be configured by the platform administrator (AI_API_KEY environment variable).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Date Format Tab Component — controls the system-wide date format used everywhere dates are displayed.
function DateFormatTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const snap = useSnapshot(adminStore);
  const current = snap.dateFormat || 'dd/MM/yyyy';

  const handleSelect = (value: string) => {
    if (value === adminStore.dateFormat) return;
    adminStore.dateFormat = value;
    showToast('Date format updated', 'success');
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl shadow-lg">
            <DocumentTextIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Date Format</h3>
            <p className="text-sm text-gray-600 mt-1">
              The format selected here is used everywhere a date is displayed in the application — asset records, risk registers,
              the SoA, the dashboard, RACI exports, user management, PDF and CSV exports. Dates are always stored in a standard
              internal format; this setting only changes how they are shown.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-gray-900">Choose a format</h4>
            <p className="text-sm text-gray-500 mt-0.5">Today previews as <span className="font-mono">{formatToday()}</span></p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DATE_FORMAT_PRESETS.map(preset => {
            const selected = preset.value === current;
            return (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleSelect(preset.value)}
                className={clsx(
                  'flex items-center justify-between gap-4 px-4 py-3 rounded-lg border text-left transition-colors',
                  selected
                    ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                )}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{preset.label}</div>
                  <div className="text-xs text-gray-500 font-mono mt-0.5">{preset.example}</div>
                </div>
                {selected && <CheckIcon className="w-5 h-5 text-cyan-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <p className="text-xs text-gray-500">
          The change applies on the next render of each page — switch tabs or refresh to see it everywhere.
        </p>
      </div>
    </div>
  );
}

// Session Timeout Tab — controls auto-logout after inactivity (per company).
const IDLE_TIMEOUT_PRESETS = [
  { value: 0, label: 'Off', description: 'Never sign out automatically' },
  { value: 15, label: '15 minutes', description: 'Tighter security' },
  { value: 30, label: '30 minutes', description: 'Balanced' },
  { value: 60, label: '60 minutes', description: 'Default' },
  { value: 120, label: '2 hours', description: 'Most lenient' },
];

function SessionTimeoutTab({ showToast }: { showToast: (message: string, type: 'success' | 'error' | 'info') => void }) {
  const snap = useSnapshot(adminStore);
  const current = typeof snap.idleTimeoutMinutes === 'number' ? snap.idleTimeoutMinutes : 60;

  const handleSelect = (value: number) => {
    if (value === adminStore.idleTimeoutMinutes) return;
    adminStore.idleTimeoutMinutes = value;
    showToast(value === 0 ? 'Auto-logout disabled' : `Auto-logout set to ${value} minutes`, 'success');
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg">
            <LockClosedIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Session Timeout</h3>
            <p className="text-sm text-gray-600 mt-1">
              Automatically sign users out after a period of inactivity. A 60-second warning appears
              first so they can stay signed in without losing work. Applies to everyone in this company.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h4 className="font-medium text-gray-900 mb-4">Sign out after</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {IDLE_TIMEOUT_PRESETS.map(preset => {
            const selected = preset.value === current;
            return (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleSelect(preset.value)}
                className={clsx(
                  'flex items-center justify-between gap-4 px-4 py-3 rounded-lg border text-left transition-colors',
                  selected
                    ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                )}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{preset.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{preset.description}</div>
                </div>
                {selected && <CheckIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <p className="text-xs text-gray-500">
          The change applies on each user's next sign-in or page refresh.
        </p>
      </div>
    </div>
  );
}

export default AdminPage;
